import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  Receipt, Landmark, Building2, Wallet, CalendarRange, LogOut,
  Scale, ScrollText, FileBarChart, TrendingUp, Banknote, Calculator, Tag,
  Milestone, Map, School, ClipboardCheck, Car, Link2, Truck
} from 'lucide-react';

// accounts-service's own FINANCE_ROLES (approve/post/reverse, and create
// cost centers/funds/fiscal periods) — kept in sync manually since it's a
// separate service's role gate, not something this app can fetch at runtime.
const ACCOUNTS_ROLES = ['Super Admin', 'Secretary', 'Director', 'Chairman', 'Deputy Director Admin & Finance'];

const menuItems = [
  { path: '/vouchers', label: 'Vouchers', icon: Receipt, roles: ACCOUNTS_ROLES },
  { path: '/chart-of-accounts', label: 'Chart of Accounts', icon: Landmark, roles: ACCOUNTS_ROLES },
  { path: '/cost-centers', label: 'Cost Centers', icon: Building2, roles: ACCOUNTS_ROLES },
  { path: '/funds', label: 'Funds', icon: Wallet, roles: ACCOUNTS_ROLES },
  { path: '/fiscal-periods', label: 'Fiscal Periods', icon: CalendarRange, roles: ACCOUNTS_ROLES },
  { path: '/voucher-types', label: 'Voucher Types', icon: Tag, roles: ACCOUNTS_ROLES },
  { path: '/phases', label: 'Phases', icon: Milestone, roles: ACCOUNTS_ROLES },
  { path: '/districts', label: 'Districts', icon: Map, roles: ACCOUNTS_ROLES },
  { path: '/institutions', label: 'Institutions', icon: School, roles: ACCOUNTS_ROLES },
  { path: '/vehicles', label: 'Vehicles', icon: Car, roles: ACCOUNTS_ROLES },
  { path: '/vendors', label: 'Vendors', icon: Truck, roles: ACCOUNTS_ROLES },
  { path: '/budgets', label: 'Budgets', icon: Calculator, roles: ACCOUNTS_ROLES },
  { path: '/trial-balance', label: 'Trial Balance', icon: Scale, roles: ACCOUNTS_ROLES, section: 'Reports' },
  { path: '/ledgers', label: 'Ledgers', icon: ScrollText, roles: ACCOUNTS_ROLES, section: 'Reports' },
  { path: '/financial-statements/position', label: 'Financial Position', icon: FileBarChart, roles: ACCOUNTS_ROLES, section: 'Reports' },
  { path: '/financial-statements/revenue-expenditure', label: 'Revenue & Expenditure', icon: TrendingUp, roles: ACCOUNTS_ROLES, section: 'Reports' },
  { path: '/financial-statements/cash-bank', label: 'Cash & Bank Position', icon: Banknote, roles: ACCOUNTS_ROLES, section: 'Reports' },
  { path: '/bank-reconciliation', label: 'Bank Reconciliation', icon: Link2, roles: ACCOUNTS_ROLES, section: 'Reports' },
  { path: '/program-reports', label: 'Program Reports', icon: ClipboardCheck, roles: ACCOUNTS_ROLES, section: 'Reports' },
];

const Sidebar = () => {
  const { user, logout } = useAuth();
  const location = useLocation();

  const allowedItems = menuItems.filter(item =>
    item.roles.includes(user?.role)
  );

  return (
    <div className="w-64 bg-blue-900 min-h-screen flex flex-col">
      {/* Logo */}
      <div className="p-6 border-b border-blue-800">
        <div className="text-white font-bold text-xl">BBSHRRDB</div>
        <div className="text-blue-300 text-xs mt-1">Accounts v1.0</div>
      </div>

      {/* User Info */}
      <div className="p-4 border-b border-blue-800">
        <div className="text-white font-medium text-sm">{user?.fullName}</div>
        <div className="text-blue-300 text-xs">{user?.role}</div>
      </div>

      {/* Menu Items */}
      <nav className="flex-1 p-4">
        {allowedItems.map((item, index) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
          const showSectionLabel = item.section && allowedItems[index - 1]?.section !== item.section;
          return (
            <React.Fragment key={item.path}>
              {showSectionLabel && (
                <div className="px-4 pt-3 pb-1 text-xs font-semibold text-blue-400 uppercase tracking-wide">
                  {item.section}
                </div>
              )}
              <Link
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg mb-1 transition text-sm
                  ${isActive
                    ? 'bg-blue-700 text-white font-semibold'
                    : 'text-blue-200 hover:bg-blue-800 hover:text-white'
                  }`}
              >
                <Icon size={18} />
                <span className="flex-1">{item.label}</span>
              </Link>
            </React.Fragment>
          );
        })}
      </nav>

      {/* Logout */}
      <div className="p-4 border-t border-blue-800">
        <button
          onClick={logout}
          className="flex items-center gap-3 px-4 py-3 rounded-lg text-blue-200 hover:bg-blue-800 hover:text-white transition text-sm w-full"
        >
          <LogOut size={18} />
          Logout
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
